import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Preloader from '../components/Preloader';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DonationInterestForm = () => {
  const navigate = useNavigate();
  const [donationData, setDonationData] = useState(null);
  const [formData, setFormData] = useState({
    interests: [],
    language: 'english',
    updateFrequency: 'monthly',
    receiveNewsletter: true,
    volunteerInterest: false,
    partnersInterest: false,
    phone: '',
    organization: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Load donation data from previous steps
    const savedData = sessionStorage.getItem('donationData');
    if (savedData) {
      setDonationData(JSON.parse(savedData));
    }

    // Initialize scripts if needed
    if (window.initPathioScripts) {
      window.initPathioScripts();
    }
  }, []);

  const interestOptions = [
    { id: 'health-updates', label: 'Health & Medical Updates' },
    { id: 'research-findings', label: 'Research Findings' },
    { id: 'success-stories', label: 'Success Stories & Impact' },
    { id: 'events', label: 'Events & Webinars' },
    { id: 'volunteering', label: 'Volunteering Opportunities' },
    { id: 'fundraising', label: 'Fundraising Campaigns' },
    { id: 'partnerships', label: 'Partnership Opportunities' },
    { id: 'financial-reports', label: 'Financial & Annual Reports' }
  ];

  const languages = [
    { value: 'english', label: 'English' },
    { value: 'uzbek', label: 'Uzbek' },
    { value: 'russian', label: 'Russian' }
  ];

  const updateFrequencies = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Bi-weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'never', label: 'No emails' }
  ];

  const handleToggleInterest = (interestId) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(i => i !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.phone && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      const updatedData = {
        ...donationData,
        ...formData
      };

      sessionStorage.setItem('donationData', JSON.stringify(updatedData));
      navigate('/donations/payment');
    }
  };

  const handleBack = () => {
    navigate('/donations/intent');
  };

  return (
    <>
      <Preloader />
      <Sidebar />
      <Header />

      <main>
        <section className="ul-breadcrumb ul-section-spacing">
          <div className="ul-container">
            <h2 className="ul-breadcrumb-title">Support Our Organization</h2>
            <ul className="ul-breadcrumb-nav">
              <li><a href="/">Home</a></li>
              <li><span className="separator"><i className="flaticon-right"></i></span></li>
              <li>Donations - Step 3</li>
            </ul>
          </div>
        </section>

        <section className="ul-donation-preferences ul-section-spacing">
          <div className="ul-container">
            {/* Progress Indicator */}
            <div className="ul-donation-progress">
              <div className="ul-progress-steps">
                <div className="ul-progress-step completed">
                  <span className="step-number">✓</span>
                  <span className="step-label">Setup</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step completed">
                  <span className="step-number">✓</span>
                  <span className="step-label">Intent</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step active">
                  <span className="step-number">3</span>
                  <span className="step-label">Preferences</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step">
                  <span className="step-number">4</span>
                  <span className="step-label">Payment</span>
                </div>
                <div className="ul-progress-line"></div>
                <div className="ul-progress-step">
                  <span className="step-number">5</span>
                  <span className="step-label">Confirm</span>
                </div>
              </div>
            </div>

            <div className="ul-donation-form-wrapper">
              <div className="ul-donation-preferences-form">
                <h2>Step 3: Your Preferences & Interests</h2>
                <p className="ul-subtitle">Help us keep you informed in the way you prefer</p>

                {/* Email Preferences Section */}
                <div className="ul-form-section">
                  <h3>📧 Communication Preferences</h3>

                  <div className="ul-form-group">
                    <label className="ul-form-label">What are you interested in?</label>
                    <div className="ul-checkbox-grid">
                      {interestOptions.map(option => (
                        <label key={option.id} className="ul-checkbox-label">
                          <input
                            type="checkbox"
                            value={option.id}
                            checked={formData.interests.includes(option.id)}
                            onChange={() => handleToggleInterest(option.id)}
                          />
                          <span className="checkbox-text">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="ul-form-group">
                    <label className="ul-form-label">Preferred Language</label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="ul-form-select"
                    >
                      {languages.map(lang => (
                        <option key={lang.value} value={lang.value}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="ul-form-group">
                    <label className="ul-form-label">Update Frequency</label>
                    <select
                      name="updateFrequency"
                      value={formData.updateFrequency}
                      onChange={handleInputChange}
                      className="ul-form-select"
                    >
                      {updateFrequencies.map(freq => (
                        <option key={freq.value} value={freq.value}>
                          {freq.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <label className="ul-checkbox-label">
                    <input
                      type="checkbox"
                      name="receiveNewsletter"
                      checked={formData.receiveNewsletter}
                      onChange={handleInputChange}
                    />
                    <span className="checkbox-text">I agree to receive newsletters and updates</span>
                  </label>
                </div>

                {/* Additional Opportunities Section */}
                <div className="ul-form-section">
                  <h3>🤝 Get More Involved</h3>

                  <label className="ul-checkbox-label">
                    <input
                      type="checkbox"
                      name="volunteerInterest"
                      checked={formData.volunteerInterest}
                      onChange={handleInputChange}
                    />
                    <span className="checkbox-text">I'm interested in volunteering opportunities</span>
                  </label>

                  <label className="ul-checkbox-label">
                    <input
                      type="checkbox"
                      name="partnersInterest"
                      checked={formData.partnersInterest}
                      onChange={handleInputChange}
                    />
                    <span className="checkbox-text">I'm interested in partnership opportunities</span>
                  </label>
                </div>

                {/* Optional Contact Info Section */}
                <div className="ul-form-section">
                  <h3>📞 Optional Contact Information</h3>

                  <div className="ul-form-group">
                    <label className="ul-form-label">Phone Number (Optional)</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 123-4567"
                      className={`ul-form-input ${errors.phone ? 'error' : ''}`}
                    />
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>

                  <div className="ul-form-group">
                    <label className="ul-form-label">Organization/Company (Optional)</label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder="If applicable"
                      className="ul-form-input"
                    />
                  </div>
                </div>

                {/* Privacy Notice */}
                <div className="ul-privacy-notice">
                  <p>
                    <strong>Privacy:</strong> Your information is protected and will never be shared with third parties. 
                    We comply with GDPR and data protection laws.
                  </p>
                </div>

                {/* Form Actions */}
                <div className="ul-form-actions">
                  <button 
                    type="button"
                    className="ul-btn-secondary"
                    onClick={handleBack}
                  >
                    ← Back
                  </button>
                  <button 
                    type="button"
                    className="ul-btn ul-btn-primary"
                    onClick={handleNext}
                  >
                    Continue to Payment →
                  </button>
                </div>
              </div>

              {/* Help Sidebar */}
              <div className="ul-donation-help">
                <div className="ul-help-card">
                  <h3>💡 Why These Questions?</h3>
                  <ul className="ul-help-list">
                    <li>We want to send you relevant updates</li>
                    <li>Personalized impact reports based on your interests</li>
                    <li>Volunteer opportunities that match your skills</li>
                    <li>Partnership opportunities for your organization</li>
                  </ul>
                </div>

                <div className="ul-info-card">
                  <h3>Your Donation Summary</h3>
                  {donationData && (
                    <>
                      <div className="ul-info-item">
                        <span>Amount:</span>
                        <strong>${donationData.amount}</strong>
                      </div>
                      <div className="ul-info-item">
                        <span>Donor:</span>
                        <strong>{donationData.name}</strong>
                      </div>
                      <div className="ul-info-item">
                        <span>Email:</span>
                        <strong>{donationData.email}</strong>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default DonationInterestForm;
